import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Heading1, Heading2, Quote, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight, RemoveFormatting } from 'lucide-react';

/**
 * Lightweight rich-text editor built on contentEditable + document.execCommand.
 * Emits HTML via onChange(html). No third-party deps required.
 *
 * Props:
 *   value:    HTML string (controlled)
 *   onChange: (html) => void
 *   height:   optional min height (default 320)
 *   placeholder: shown when empty
 */
const RichTextEditor = ({ value = '', onChange, height = 320, placeholder = 'Compose your email…' }) => {
  const ref = useRef(null);

  // Sync external value → editor when the incoming value diverges (e.g. loading a template).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if ((el.innerHTML || '') !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command, arg) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || '');
  };

  const onInput = () => onChange?.(ref.current?.innerHTML || '');

  const insertLink = () => {
    const url = window.prompt('Enter URL (include https://)');
    if (!url) return;
    exec('createLink', url);
  };

  const btn = (title, icon, onClick) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="px-2 py-1 rounded hover:bg-gray-100 text-gray-700 border border-transparent hover:border-gray-200 transition"
    >
      {icon}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        {btn('Heading 1', <Heading1 className="w-4 h-4" />, () => exec('formatBlock', '<H1>'))}
        {btn('Heading 2', <Heading2 className="w-4 h-4" />, () => exec('formatBlock', '<H2>'))}
        {btn('Paragraph', <span className="text-xs font-medium">P</span>, () => exec('formatBlock', '<P>'))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        {btn('Bold', <Bold className="w-4 h-4" />, () => exec('bold'))}
        {btn('Italic', <Italic className="w-4 h-4" />, () => exec('italic'))}
        {btn('Underline', <Underline className="w-4 h-4" />, () => exec('underline'))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        {btn('Bulleted list', <List className="w-4 h-4" />, () => exec('insertUnorderedList'))}
        {btn('Numbered list', <ListOrdered className="w-4 h-4" />, () => exec('insertOrderedList'))}
        {btn('Quote', <Quote className="w-4 h-4" />, () => exec('formatBlock', '<BLOCKQUOTE>'))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        {btn('Align left', <AlignLeft className="w-4 h-4" />, () => exec('justifyLeft'))}
        {btn('Align center', <AlignCenter className="w-4 h-4" />, () => exec('justifyCenter'))}
        {btn('Align right', <AlignRight className="w-4 h-4" />, () => exec('justifyRight'))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        {btn('Link', <LinkIcon className="w-4 h-4" />, insertLink)}
        {btn('Clear formatting', <RemoveFormatting className="w-4 h-4" />, () => exec('removeFormat'))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        {btn('Undo', <Undo2 className="w-4 h-4" />, () => exec('undo'))}
        {btn('Redo', <Redo2 className="w-4 h-4" />, () => exec('redo'))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onBlur={onInput}
        data-placeholder={placeholder}
        className="px-4 py-3 outline-none prose prose-sm max-w-none focus:ring-0 empty-placeholder"
        style={{ minHeight: `${height}px` }}
      />
      <style>{`
        .empty-placeholder:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        [contenteditable] h1 { font-size: 1.5rem; font-weight: 700; margin: 0.6em 0 0.3em; }
        [contenteditable] h2 { font-size: 1.25rem; font-weight: 700; margin: 0.6em 0 0.3em; }
        [contenteditable] p { margin: 0.4em 0; }
        [contenteditable] ul { list-style: disc; margin-left: 1.4em; }
        [contenteditable] ol { list-style: decimal; margin-left: 1.4em; }
        [contenteditable] blockquote { border-left: 3px solid #E5E7EB; padding-left: 1em; color: #6B7280; margin: 0.5em 0; }
        [contenteditable] a { color: #2563EB; text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
